import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { loginUser, registerUser, clearAuthError } from '../../store/actions/authActions';
import GoogleSignInButton from './GoogleSignInButton';
import TermsAcceptance from './TermsAcceptance';
import { goToVerifyEmailPending } from '../../helpers/authNavigation';
import * as authService from '../../services/authService';
import { getTesterProfile, saveTesterUnlock } from '../../helpers/testerAccess';
import '../start/PasswordGate.css';
import './LoginPage.css';

const focusRowInput = (e) => {
  const row = e.currentTarget;
  const input = row && row.querySelector('input');
  if (input && !input.disabled) {
    input.focus();
  }
};

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C2 12 5.5 5 12 5C18.5 5 22 12 22 12C22 12 18.5 19 12 19C5.5 19 2 12 2 12Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M17.94 17.94C16.22 19.26 14.17 20 12 20C5.5 20 2 13 2 13C3.07 10.94 4.76 9.28 6.76 8.18M9.9 6.64C10.58 6.46 11.28 6.36 12 6.36C18.5 6.36 22 13 22 13C21.27 14.55 20.18 15.92 18.88 17.02M14.12 14.12C13.79 14.45 13.41 14.71 12.99 14.88"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M2 2L22 22" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

/**
 * Login / Create Account ΓÇö entry after gate for new users.
 */
const LoginPage = ({
  bootstrapped,
  loading,
  error,
  loginUserAction,
  registerUserAction,
  clearAuthErrorAction,
}) => {
  const history = useHistory();
  const location = useLocation();
  const initialMode = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const q = (params.get('mode') || '').toLowerCase();
    if (q === 'register' || q === 'signup') return 'register';
    if (q === 'forgot') return 'forgot';
    return 'login';
  }, [location.search]);

  // The User Access gate already collected an email and a chosen password;
  // carry them over so Create Account does not ask for the same thing again.
  const prefill = (location.state || {});

  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState(prefill.prefillEmail || '');
  const [password, setPassword] = useState(prefill.prefillPassword || '');
  const [confirmPassword, setConfirmPassword] = useState(prefill.prefillPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [info, setInfo] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const profile = getTesterProfile();
    if (profile) {
      if (profile.name) setName(profile.name);
      if (profile.email) setEmail(profile.email);
    }
  }, []);

  useEffect(() => {
    clearAuthErrorAction();
  }, [clearAuthErrorAction]);

  const switchMode = (next) => {
    setMode(next);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setConfirmPassword('');
    setTermsAccepted(false);
    setLocalError('');
    setInfo('');
    clearAuthErrorAction();
    if (next === 'login') {
      history.replace('/login');
    } else if (next === 'register') {
      history.replace('/login?mode=register');
    }
  };

  const handleResend = async () => {
    setLocalError('');
    try {
      const target = pendingVerifyEmail || email.trim();
      const data = await authService.resendVerification({ email: target });
      setInfo(data.message || 'Verification email sent. Please check your inbox.');
    } catch (err) {
      setLocalError(err.message || 'Could not resend verification email');
    }
  };

  // /login always shows Login — never redirect away based on prior session/flow.
  if (!bootstrapped) {
    return (
      <div className="xk-gate">
        <div className="text-center">
          <div className="spinner-border text-success" role="status" />
          <p className="mt-3 text-muted mb-0">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setInfo('');
    clearAuthErrorAction();

    if (!email.trim()) {
      setLocalError('Please enter your email.');
      return;
    }

    if (mode === 'forgot') {
      setForgotBusy(true);
      try {
        const data = await authService.forgotPassword({ email: email.trim() });
        if (data.devResetToken) {
          history.push(`/reset-password?token=${encodeURIComponent(data.devResetToken)}`);
          return;
        }
        let msg = data.message || 'Check your email for a reset link.';
        if (data.devResetUrl) {
          msg += ` Dev link: ${data.devResetUrl}`;
        }
        setInfo(msg);
      } catch (err) {
        setLocalError(err.message || 'Could not send reset link');
      } finally {
        setForgotBusy(false);
      }
      return;
    }

    if (mode === 'register') {
      if (!name.trim()) {
        setLocalError('Please enter your name.');
        return;
      }
      if (!password) {
        setLocalError('Please set your account password.');
        return;
      }
      if (password.length < 8) {
        setLocalError('Password must be at least 8 characters.');
        return;
      }
      if (!confirmPassword) {
        setLocalError('Please confirm your password.');
        return;
      }
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match.');
        return;
      }
      if (!termsAccepted) {
        setLocalError('Please accept the Terms & Conditions to create your account.');
        return;
      }

      const gateProfile = getTesterProfile();
      saveTesterUnlock({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        organization: (gateProfile && gateProfile.organization) || '',
        country: (gateProfile && gateProfile.country) || '',
      });

      try {
        const data = await registerUserAction({
          email: email.trim(),
          password,
          name: name.trim(),
          acceptTerms: true,
        });

        goToVerifyEmailPending(history, email.trim());
        return;
      } catch (err) {
        if (err.errorCode === 'ERR_EMAIL_EXISTS') {
          switchMode('login');
          setLocalError('');
          setInfo('This email is already registered. Log in with your password.');
          return;
        }
        setLocalError(err.message || 'Registration failed');
      }
      return;
    }

    if (!password) {
      setLocalError('Please enter your password.');
      return;
    }

    try {
      const result = await loginUserAction({ email: email.trim(), password });
      history.replace(result.redirectTo || '/subscription');
    } catch (err) {
      if (err.errorCode === 'ERR_ACCOUNT_NOT_FOUND') {
        setLocalError('');
        setInfo('No account found with this email. Use Register to create one.');
        return;
      }
      if (err.errorCode === 'ERR_EMAIL_NOT_VERIFIED') {
        goToVerifyEmailPending(history, email.trim());
        return;
      }
      if (err.errorCode === 'ERR_INVALID_CREDENTIALS') {
        setLocalError(
          'Incorrect password. Use Forgot password below to set a new one (dev: reset link opens automatically).'
        );
        return;
      }
      setLocalError(err.message || 'Login failed');
    }
  };

  const displayError = localError || error;
  const isBusy = loading || forgotBusy;

  const title =
    mode === 'register' ? 'Create Account' : mode === 'forgot' ? 'Forgot Password' : 'Login';
  const submitLabel =
    isBusy
      ? 'Please wait...'
      : mode === 'register'
        ? 'Create Account'
        : mode === 'forgot'
          ? 'Send Reset Link'
          : 'Login';

  return (
    <div className="xk-gate">
      <div className="xk-gate__card xk-login-card">
        <img
          src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
          alt="XLog3D"
          className="xk-gate__logo"
        />

        <div className="xk-gate__note">
          <div className="xk-gate__note-title">Attention Please!</div>
          <p>Please use computer to run the App</p>
        </div>

        <h2 className="xk-gate__title">{title}</h2>

        <form className="xk-gate__form" onSubmit={handleSubmit} autoComplete="off" noValidate>
          {mode === 'register' && (
            <div className="xk-gate__row" onMouseDown={focusRowInput}>
              <label className="xk-gate__row-label" htmlFor="auth-name">Name</label>
              <input
                id="auth-name"
                className="xk-gate__row-input"
                type="text"
                value={name}
                disabled={isBusy}
                autoFocus
                autoComplete="off"
                onChange={(e) => {
                  setName(e.target.value);
                  if (displayError) setLocalError('');
                }}
              />
            </div>
          )}

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              className="xk-gate__row-input"
              type="text"
              inputMode="email"
              value={email}
              disabled={isBusy}
              autoFocus={mode !== 'register'}
              autoComplete="off"
              onChange={(e) => {
                setEmail(e.target.value);
                if (displayError) setLocalError('');
              }}
            />
          </div>

          {mode !== 'forgot' && (
            <div className="xk-gate__row xk-login-password-row" onMouseDown={focusRowInput}>
              <label className="xk-gate__row-label" htmlFor="auth-password">
                Password
              </label>
              <div className="xk-login-password-field">
                <input
                  id="auth-password"
                  className="xk-gate__row-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  disabled={isBusy}
                  autoComplete="off"
                  placeholder={mode === 'register' ? 'Set your account password' : ''}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (displayError) setLocalError('');
                  }}
                />
                <button
                  type="button"
                  className="xk-login-password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  disabled={isBusy}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div className="xk-gate__row xk-login-password-row" onMouseDown={focusRowInput}>
              <label className="xk-gate__row-label" htmlFor="auth-confirm-password">
                Confirm Password
              </label>
              <div className="xk-login-password-field">
                <input
                  id="auth-confirm-password"
                  className="xk-gate__row-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  disabled={isBusy}
                  autoComplete="off"
                  placeholder="Re-enter your password"
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (displayError) setLocalError('');
                  }}
                />
                <button
                  type="button"
                  className="xk-login-password-toggle"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  disabled={isBusy}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
          )}

          {mode === 'register' && (
            <TermsAcceptance
              id="auth-terms"
              checked={termsAccepted}
              disabled={isBusy}
              onChange={(value) => {
                setTermsAccepted(value);
                if (displayError) setLocalError('');
              }}
            />
          )}

          {displayError && <div className="xk-gate__error">{displayError}</div>}
          {info && <div className="xk-login-info">{info}</div>}

          <button
            type="submit"
            className="xk-gate__submit"
            disabled={isBusy || (mode === 'register' && !termsAccepted)}
          >
            {submitLabel}
          </button>

          {mode !== 'forgot' && (
            <>
              {mode === 'register' && !termsAccepted && (
                <p className="xk-terms-hint">Accept the Terms &amp; Conditions to continue.</p>
              )}
              <div className="xk-gate__divider"><span>or</span></div>
              {mode === 'login' || termsAccepted ? (
                <GoogleSignInButton
                  acceptTerms={mode === 'register' && termsAccepted}
                  onError={(message, err) => {
                    if (err && err.errorCode === 'ERR_TERMS_NOT_ACCEPTED') {
                      switchMode('register');
                      setLocalError('Accept the Terms & Conditions to create an account with Google.');
                      return;
                    }
                    setLocalError(message);
                  }}
                />
              ) : (
                <p className="xk-terms-hint xk-terms-hint--center">
                  Accept the Terms &amp; Conditions above to sign up with Google.
                </p>
              )}
            </>
          )}
        </form>

        <div className="xk-login-links">
          {mode === 'login' && (
            <>
              <button
                type="button"
                className="xk-login-link"
                disabled={isBusy}
                onClick={() => switchMode('forgot')}
              >
                Forgot password?
              </button>
              <button
                type="button"
                className="xk-login-link xk-login-link--register"
                disabled={isBusy}
                onClick={() => history.push('/access')}
              >
                <span className="xk-login-link__label">Need an account?</span>{' '}
                <span className="xk-login-link__action">Register</span>
              </button>
            </>
          )}
          {mode === 'register' && (
            <button
              type="button"
              className="xk-login-link"
              disabled={isBusy}
              onClick={() => switchMode('login')}
            >
              Already have an account? Log in
            </button>
          )}
          {mode === 'forgot' && (
            <button
              type="button"
              className="xk-login-link"
              disabled={isBusy}
              onClick={() => switchMode('login')}
            >
              Back to Login
            </button>
          )}
          {(pendingVerifyEmail ||
            (displayError && String(displayError).toLowerCase().includes('verify'))) && (
            <button
              type="button"
              className="xk-login-link xk-login-link--outline"
              disabled={isBusy}
              onClick={handleResend}
            >
              Resend verification email
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

LoginPage.propTypes = {
  bootstrapped: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  loginUserAction: PropTypes.func.isRequired,
  registerUserAction: PropTypes.func.isRequired,
  clearAuthErrorAction: PropTypes.func.isRequired,
};

LoginPage.defaultProps = {
  bootstrapped: false,
  loading: false,
  error: null,
};

const mapStateToProps = (state) => ({
  bootstrapped: state.auth.bootstrapped,
  loading: state.auth.loading,
  error: state.auth.error,
});

const mapDispatchToProps = {
  loginUserAction: loginUser,
  registerUserAction: registerUser,
  clearAuthErrorAction: clearAuthError,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
